/**
 * Гард менеджерских роутов (P1-RBAC, integration/forms-types-rbac).
 *
 * Менеджерские поверхности (Персонал /staff*, Настройки /settings*, «Все
 * счёта» /billing) принадлежат ТОЛЬКО управляющим ролям (owner/manager).
 * Рядовой персонал (front_desk/housekeeper/restaurant/maintenance/staff) и
 * гость доходили до полного менеджерского экрана (дефект monkey-прогона) и
 * дёргали /v1/staff/* и /v1/folios. Бэк теперь отдаёт им 403, но экран всё
 * равно рендерился пустым/ломаным — оборачиваем такие роуты этим гардом и
 * показываем карточку «Нет доступа» (как /settings/roles).
 *
 * ВАЖНО (патент 403 ≠ logout): здесь НИКАКИХ clearLocalTokens/redirect —
 * пользователь остаётся залогинен, просто видит «нет прав».
 */
import { Card } from '@hsc/ui';
import { usePlatformMe } from '../api/usePlatformMe.js';
import { resolveDashboardKind } from './resolve-role.js';

/** Карточка «Нет доступа» — единый вид для запрета по роли. */
export function NoAccessCard(): JSX.Element {
  return (
    <section data-testid="role-forbidden">
      <Card>
        <h2 className="hsc-card__title">Нет доступа</h2>
        <p>
          У Вашей роли нет прав на этот раздел. Если это ошибка — обратитесь к
          управляющему отеля.
        </p>
      </Card>
    </section>
  );
}

export interface RequireManagementRoleProps {
  children: JSX.Element;
}

/**
 * Пускает к children ТОЛЬКО owner/manager. Остальным (персонал/гость) —
 * карточка «Нет доступа». Пока профиль грузится — ничего не рендерим
 * (избегаем мигания запрета до резолва роли).
 */
export function RequireManagementRole({
  children,
}: RequireManagementRoleProps): JSX.Element | null {
  const { data, isLoading } = usePlatformMe();
  if (isLoading || !data) return null;
  const kind = resolveDashboardKind(data.actor.roles);
  if (kind === 'owner' || kind === 'manager') return children;
  return <NoAccessCard />;
}

/**
 * Гард «Выдать гостю доступ» (Phase 3 гост-входа): owner/manager/front_desk
 * (право guest_access.create). front_desk сворачивается в dashboard-kind
 * `staff`, поэтому проверяем сырые роли actor.roles напрямую (а не kind).
 */
const FRONT_DESK_ROLES = new Set(['tenant_owner', 'owner', 'hotel_manager', 'manager', 'front_desk']);

export function RequireFrontDeskRole({
  children,
}: RequireManagementRoleProps): JSX.Element | null {
  const { data, isLoading } = usePlatformMe();
  if (isLoading || !data) return null;
  const allowed = data.actor.roles.some((r) => FRONT_DESK_ROLES.has(r));
  if (allowed) return children;
  return <NoAccessCard />;
}
